import React from 'react';
import PageSeo from './components/PageSeo';
import { useDarkMode } from './DarkModeContext';
import Footer from './Footer';

function NegotiationBrainManifesto() {
    const { darkMode } = useDarkMode();

    return (
        <>
        <PageSeo
            title="The Negotiation Brain Manifesto – Merra AI"
            description="The Negotiation Brain Manifesto—a deep dive into how to do the thing before you feel ready."
            path="/negotiation-brain-manifesto"
            keywords="negotiation brain, negotiation psychology, decision making, negotiation manifesto, negotiation skills"
        />
        <div style={{
            minHeight: '100vh',
            backgroundColor: darkMode ? '#1a1a1a' : '#ffffff',
            color: darkMode ? '#e0e0e0' : '#333',
            padding: '40px 20px',
            lineHeight: '1.8'
        }}>
            <div style={{
                maxWidth: '800px',
                margin: '0 auto'
            }}>
                <h1 style={{
                    fontSize: '48px',
                    fontWeight: '700',
                    marginBottom: '20px',
                    color: darkMode ? '#ffffff' : '#2c3e50',
                    lineHeight: '1.2'
                }}>
                    🧠 The Negotiation Brain Manifesto
                </h1>
                
                <p style={{
                    fontSize: '20px',
                    marginBottom: '40px',
                    fontStyle: 'italic',
                    color: darkMode ? '#ccc' : '#666'
                }}>
                    <strong>For all those who know exactly what they should do, yet somehow don't do it.</strong>
                </p>

                <div style={{ marginBottom: '40px' }}>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        There are two versions of you.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        The one who wants the outcome.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        And the one who negotiates with the work required to get there.
                    </p>
                </div>

                <div style={{ marginBottom: '40px' }}>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        Every day you wake up with a mission.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        And every day, your brain tries to renegotiate that mission.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        It whispers:
                    </p>
                    <ul style={{ 
                        fontSize: '18px', 
                        marginLeft: '30px',
                        marginBottom: '20px',
                        listStyleType: 'none'
                    }}>
                        <li style={{ marginBottom: '10px' }}>"Later."</li>
                        <li style={{ marginBottom: '10px' }}>"Not ready."</li>
                        <li style={{ marginBottom: '10px' }}>"Need more information."</li>
                        <li style={{ marginBottom: '10px' }}>"Let's tweak one more thing."</li>
                        <li style={{ marginBottom: '10px' }}>"Let's reorganize the structure before starting."</li>
                        <li style={{ marginBottom: '10px' }}>"Let's watch some inspiration first."</li>
                        <li style={{ marginBottom: '10px' }}>"Let's fix something small instead, so we still feel productive."</li>
                    </ul>
                </div>

                <div style={{ marginBottom: '40px' }}>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        This is <strong>The Negotiation Brain.</strong>
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        And if you don't understand it, it will run your life.
                    </p>
                </div>

                <div style={{ marginBottom: '40px' }}>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        The Negotiation Brain is not lazy.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        It is not weak.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        It is not unambitious.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        It is <strong>protective.</strong>
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        It is trying to keep you safe by keeping you still.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                        Safe from judgment.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                        Safe from discomfort.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                        Safe from failure.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        Safe from success, too.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        It knows that the moment you move, things change.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                        You become visible.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                        You become vulnerable.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        You become accountable.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        So it creates infinite micro-delays that feel logical, responsible, even smart.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        But they are traps.
                    </p>
                </div>

                <div style={{ 
                    marginBottom: '40px',
                    padding: '20px',
                    borderLeft: `4px solid ${darkMode ? '#4CAF50' : '#2c3e50'}`,
                    backgroundColor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(44, 62, 80, 0.05)'
                }}>
                    <p style={{ fontSize: '20px', fontWeight: '600', marginBottom: '0' }}>
                        <strong>The Negotiation Brain prevents momentum by creating the illusion of progress.</strong>
                    </p>
                </div>

                <div style={{ marginBottom: '40px' }}>
                    <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                        You tidy your desk instead of writing.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                        You rebuild your landing page instead of posting content.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                        You research competitors instead of talking to users.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                        You fix a tiny bug instead of shipping the feature people want.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        You prepare for the perfect moment instead of creating the moment.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        And every time you give in, you teach your brain that negotiating works.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                        You reward avoidance.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        You strengthen the habit.
                    </p>
                </div>

                <div style={{ marginBottom: '40px' }}>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        But here is the truth that founders and creators learn the hard way:
                    </p>
                    <div style={{
                        padding: '20px',
                        borderLeft: `4px solid ${darkMode ? '#ffc107' : '#856404'}`,
                        backgroundColor: darkMode ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.15)',
                        marginBottom: '20px'
                    }}>
                        <p style={{ fontSize: '22px', fontWeight: '600', marginBottom: '0', fontStyle: 'italic' }}>
                            <strong>Momentum does not come from clarity.</strong>
                        </p>
                        <p style={{ fontSize: '22px', fontWeight: '600', marginTop: '10px', fontStyle: 'italic' }}>
                            <strong>Clarity comes from momentum.</strong>
                        </p>
                    </div>
                    <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                        You don't think your way into movement.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                        You move your way into thinking.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                        Action creates proof.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                        Proof creates confidence.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        Confidence destroys negotiation.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        So here is the manifesto:
                    </p>
                </div>

                <div style={{ 
                    marginTop: '60px',
                    marginBottom: '40px',
                    borderTop: darkMode ? '2px solid #333' : '2px solid #ddd',
                    paddingTop: '40px'
                }}>
                    <h2 style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        marginBottom: '30px',
                        color: darkMode ? '#ffffff' : '#2c3e50'
                    }}>
                        🔥 1. Do the thing before you're ready
                    </h2>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        Ready is a feeling, not a requirement.
                    </p>
                    <p style={{ fontSize: '18px', marginBottom: '40px' }}>
                        And feelings are negotiators.
                    </p>
                    <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '40px' }}>
                        Do it anyway.
                    </p>
                </div>

                <div style={{ marginBottom: '40px' }}>
                    <h2 style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        marginBottom: '30px',
                        color: darkMode ? '#ffffff' : '#2c3e50'
                    }}>
                        ⚡ 2. Your brain is allowed to negotiate. You just don't negotiate back.
                    </h2>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        Acknowledge the excuses.
                    </p>
                    <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '40px' }}>
                        Then act while they're still talking.
                    </p>
                </div>

                <div style={{ marginBottom: '40px' }}>
                    <h2 style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        marginBottom: '30px',
                        color: darkMode ? '#ffffff' : '#2c3e50'
                    }}>
                        🚀 3. Momentum over perfection
                    </h2>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        Perfection is procrastination wearing makeup.
                    </p>
                    <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '40px' }}>
                        Momentum is imperfect, sloppy, fearless forward motion.
                    </p>
                </div>

                <div style={{ marginBottom: '40px' }}>
                    <h2 style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        marginBottom: '30px',
                        color: darkMode ? '#ffffff' : '#2c3e50'
                    }}>
                        🎙️ 4. Visibility is a superpower
                    </h2>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        You don't grow by improving quietly.
                    </p>
                    <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '40px' }}>
                        You grow by showing up loudly.
                    </p>
                </div>

                <div style={{ marginBottom: '40px' }}>
                    <h2 style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        marginBottom: '30px',
                        color: darkMode ? '#ffffff' : '#2c3e50'
                    }}>
                        🏋️ 5. Discipline is not rigidity. It is a refusal to be emotionally led.
                    </h2>
                    <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '40px' }}>
                        You can feel doubt and still do the work.
                    </p>
                </div>

                <div style={{ marginBottom: '40px' }}>
                    <h2 style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        marginBottom: '30px',
                        color: darkMode ? '#ffffff' : '#2c3e50'
                    }}>
                        🌊 6. Every day you don't move, resistance grows
                    </h2>
                    <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '40px' }}>
                        Every day you do move, momentum compounds.
                    </p>
                </div>

                <div style={{ marginBottom: '40px' }}>
                    <h2 style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        marginBottom: '30px',
                        color: darkMode ? '#ffffff' : '#2c3e50'
                    }}>
                        🔥 7. The people who win aren't the smartest. They are the ones who do the thing even when their brain screams no.
                    </h2>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        The world rewards movement, not hesitation.
                    </p>
                </div>

                <div style={{ marginBottom: '40px' }}>
                    <h2 style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        marginBottom: '30px',
                        color: darkMode ? '#ffffff' : '#2c3e50'
                    }}>
                        📹 8. If the story isn't perfect, post it anyway
                    </h2>
                    <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '40px' }}>
                        The imperfect version is infinitely more powerful than the nonexistent perfect one.
                    </p>
                </div>

                <div style={{ marginBottom: '40px' }}>
                    <h2 style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        marginBottom: '30px',
                        color: darkMode ? '#ffffff' : '#2c3e50'
                    }}>
                        🧩 9. Your Negotiation Brain is not your enemy. It's your training partner.
                    </h2>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        It tests your commitment every day.
                    </p>
                    <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '40px' }}>
                        Pass the test by doing the work.
                    </p>
                </div>

                <div style={{ marginBottom: '40px' }}>
                    <h2 style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        marginBottom: '30px',
                        color: darkMode ? '#ffffff' : '#2c3e50'
                    }}>
                        🌍 10. The life you want is on the other side of "I'll do it later."
                    </h2>
                    <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                        Later is the graveyard of momentum.
                    </p>
                    <p style={{ fontSize: '18px', fontWeight: '600', marginBottom: '40px' }}>
                        Now is where everything changes.
                    </p>
                </div>

                <div style={{
                    marginTop: '60px',
                    marginBottom: '40px',
                    padding: '30px',
                    backgroundColor: darkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.1)',
                    borderLeft: `4px solid #4CAF50`,
                    borderRadius: '8px'
                }}>
                    <p style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
                        The world will not wait for you to feel ready.
                    </p>
                    <p style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
                        And your dreams won't negotiate forever.
                    </p>
                    <div style={{ marginTop: '30px' }}>
                        <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                            Stand up.
                        </p>
                        <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                            Do the thing.
                        </p>
                        <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                            Post the thing.
                        </p>
                        <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                            Ship the thing.
                        </p>
                        <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                            Ask the customer.
                        </p>
                        <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                            Start the flywheel.
                        </p>
                        <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                            Break the negotiation cycle with action.
                        </p>
                    </div>
                    <div style={{ marginTop: '30px' }}>
                        <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                            This is how builders win.
                        </p>
                        <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                            This is how brands are born.
                        </p>
                        <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                            This is how momentum becomes identity.
                        </p>
                        <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                            This is how you silence the Negotiation Brain—
                        </p>
                        <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                            Not by arguing with it.
                        </p>
                        <p style={{ fontSize: '18px', fontWeight: '600' }}>
                            But by moving faster than it can bargain.
                        </p>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
        </>
    );
}

export default NegotiationBrainManifesto;

